<script setup lang="ts">
import { computed, ref } from "vue";
import type { EntryWithScores } from "@shared/types";
import Flag from "../common/Flag.vue";
import Medal from "../common/Medal.vue";
import StatusBadge from "../common/StatusBadge.vue";
import EntryBreakdown from "./EntryBreakdown.vue";

const props = defineProps<{ entries: EntryWithScores[] }>();

const expandedId = ref<number | null>(null);
function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id;
}

type SortKey = "default" | "name" | "mainPoints" | "cardsTotal" | "goalsConceded";
const sortKey = ref<SortKey>("default");
const sortDir = ref<"asc" | "desc">("desc");

function toggle(key: SortKey, naturalDir: "asc" | "desc") {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortDir.value = naturalDir;
  }
}

const sorted = computed(() => {
  if (sortKey.value === "default") return props.entries;
  const arr = [...props.entries];
  const dir = sortDir.value === "asc" ? 1 : -1;
  arr.sort((a, b) => {
    if (sortKey.value === "name") return a.name.localeCompare(b.name) * dir;
    const av = a[sortKey.value as "mainPoints" | "cardsTotal" | "goalsConceded"];
    const bv = b[sortKey.value as "mainPoints" | "cardsTotal" | "goalsConceded"];
    if (av !== bv) return (av - bv) * dir;
    return a.id - b.id;
  });
  return arr;
});

function header(label: string, key: SortKey, naturalDir: "asc" | "desc") {
  return { label, key, naturalDir };
}
const headers = [
  header("#", "default", "desc"),
  header("Name", "name", "asc"),
  header("Teams", "default", "desc"),
  header("Pts", "mainPoints", "desc"),
  header("Cards", "cardsTotal", "desc"),
  header("Conc", "goalsConceded", "desc"),
];

function arrow(key: SortKey) {
  if (sortKey.value !== key) return "";
  return sortDir.value === "asc" ? " ▲" : " ▼";
}
</script>

<template>
  <div class="hidden sm:block overflow-x-auto rounded-xl ring-1 ring-slate-200 bg-white shadow-sm">
    <table class="min-w-full text-sm">
      <thead class="bg-slate-50 text-slate-500 border-b border-slate-200">
        <tr>
          <th
            v-for="h in headers"
            :key="h.label + h.key"
            scope="col"
            :class="[
              'px-3 py-2.5 text-left font-semibold uppercase tracking-wider text-xs select-none',
              h.key !== 'default' && 'cursor-pointer hover:text-slate-900',
            ]"
            @click="h.key !== 'default' && toggle(h.key, h.naturalDir)"
          >
            {{ h.label }}<span class="tabular">{{ arrow(h.key) }}</span>
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-slate-100">
        <template v-for="(e, i) in sorted" :key="e.id">
          <tr
            class="hover:bg-amber-50/40 transition-colors cursor-pointer"
            :aria-expanded="expandedId === e.id"
            @click="toggleExpand(e.id)"
          >
            <td class="px-3 py-2.5">
              <div class="flex items-center gap-2">
                <span
                  class="text-slate-400 text-xs transition-transform select-none"
                  :class="expandedId === e.id && 'rotate-90'"
                  aria-hidden="true"
                >▶</span>
                <Medal v-if="sortKey === 'default'" :rank="i + 1" />
                <span v-else class="tabular text-slate-400">{{ i + 1 }}</span>
              </div>
            </td>
            <td class="px-3 py-2.5 font-medium text-slate-900">{{ e.name }}</td>
            <td class="px-3 py-2.5">
              <div class="flex items-start gap-1.5">
                <div
                  v-for="team in [e.pot1, e.pot2, e.pot3]"
                  :key="team.id"
                  class="flex flex-col items-center gap-0.5"
                >
                  <Flag :code="team.flag_code" :name="team.name" size="sm" />
                  <StatusBadge :team="team" size="xs" />
                </div>
              </div>
            </td>
            <td class="px-3 py-2.5 tabular font-bold text-slate-900">{{ e.mainPoints }}</td>
            <td class="px-3 py-2.5 tabular text-slate-600">
              <span class="inline-flex items-center gap-2 whitespace-nowrap">
                <span title="Yellow cards">🟨 {{ e.yellows }}</span>
                <span title="Red cards">🟥 {{ e.reds }}</span>
              </span>
            </td>
            <td class="px-3 py-2.5 tabular text-slate-600">{{ e.goalsConceded }}</td>
          </tr>
          <tr v-if="expandedId === e.id">
            <td :colspan="headers.length" class="p-0">
              <EntryBreakdown :entry="e" />
            </td>
          </tr>
        </template>
        <tr v-if="!sorted.length">
          <td colspan="6" class="px-3 py-8 text-center text-slate-400">No entries yet.</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
